import * as PoliciesApi from "@/api/usePolicies"
import { App } from "@/atoms"
import { RESOURCES_NAMES } from "@/const/resource.constants"
import { capitalizeFirstLetter } from "@/helpers/string.helpers"
import { useLogger } from "@/hooks/useLogger"
import { useServices } from "@/hooks/useServices"
import { useTranslation } from "@/hooks/useTranslation"
import { DeleteConfirmModal, useConfirmModal } from "@/molecules/ConfirmModal"
import {
  getConditionsOptions,
  getPoliciesScm,
  getScopeOptionsScm
} from "./usePolicies.selector"

import type {
  ConditionsOptionsPoliciesResponse,
  Policy,
  PolicyCategory,
  PolicyType,
  SubType
} from "@/api/usePolicies.types"
import type { Config, Params } from "@/hooks/useHttp"

const ciHooks = {
  useGet: PoliciesApi.useGetCIPolicies,
  useCreate: PoliciesApi.useCreateCIPolicy,
  useUpdate: PoliciesApi.useUpdateCIPolicy,
  useDelete: PoliciesApi.useDeleteCIPolicy,
  useScopeOptions: PoliciesApi.useScopeOptionsCI,
  useConditionsOptions: PoliciesApi.useCIConditionsOptions,
  queryUrl: RESOURCES_NAMES.SETTINGS.CI_POLICIES
}

const hooksMap = {
  code: {
    useGet: PoliciesApi.useGetPolicies,
    useCreate: PoliciesApi.useCreatePolicy,
    useUpdate: PoliciesApi.useUpdatePolicy,
    useDelete: PoliciesApi.useDeletePolicy,
    useScopeOptions: PoliciesApi.useScopeOptionsSCM,
    useConditionsOptions: PoliciesApi.useConditionsOptions,
    queryUrl: RESOURCES_NAMES.SETTINGS.POLICIES
  },
  scm: {
    useGet: PoliciesApi.useGetPolicies,
    useCreate: PoliciesApi.useCreatePolicy,
    useUpdate: PoliciesApi.useUpdatePolicy,
    useDelete: PoliciesApi.useDeletePolicy,
    useScopeOptions: PoliciesApi.useScopeOptionsSCM,
    useConditionsOptions: PoliciesApi.useConditionsOptions,
    queryUrl: RESOURCES_NAMES.SETTINGS.POLICIES
  },
  ci: ciHooks,
  "ci-sca": ciHooks,
  "ci-sast": ciHooks
}

interface Attr {
  type: PolicyType
  subType: SubType
}

export const getPolicyCategory = (type: PolicyType): PolicyCategory =>
  type === "code" || type === "ci-sast" ? "Code" : "OpenSource"

export const getPolicySubType = (type: SubType): Policy["policyType"] =>
  capitalizeFirstLetter(type)

export const usePolicies = (
  attr: {
    type: PolicyType
    subType: SubType
    needle?: string
    policyCategory?: PolicyCategory
  },
  config?: Config<ReturnType<typeof getPoliciesScm>>
) => {
  const { type, subType, needle } = attr

  const params: {
    policyType: string
    needle?: string
    policyCategory?: PolicyCategory
  } = {
    needle,
    policyType: getPolicySubType(subType),
    policyCategory: getPolicyCategory(type)
  }

  const { useGet } = hooksMap[type]
  const { response: scopeOptions, isLoading: scopeOptionsIsLoading } =
    useScopeOptions(type)
  const options = needle ? { staleTime: 0, gcTime: 0 } : {}
  const { enabled = true, ...restConfig } = config || {}
  const { isLoading, ...context } = useGet(params, {
    select: (data) => getPoliciesScm(data, scopeOptions),
    enabled: !scopeOptionsIsLoading && !!enabled,
    ...options,
    ...restConfig
  })

  return {
    isLoading: scopeOptionsIsLoading || isLoading,
    ...context
  }
}

export const useCreatePolicy = (type: PolicyType, params?: Params) => {
  const { logger } = useLogger()
  const { useCreate, queryUrl } = hooksMap[type]
  const context = useCreate(params, undefined, (queryClient) => ({
    onSuccess: (response) => {
      try {
        const { id, policyType = "Protect" } = response.data[0] // default value "Protect" because CI policies have different response structure

        queryClient.setQueryData([`${queryUrl.LIST}/${id}`, {}], response)
        queryClient.refetchQueries({
          queryKey: [queryUrl.LIST, { policyType }]
        })
      } catch (err) {
        logger.error(err as Error)
      }

      return Promise.resolve(response)
    }
  }))

  return context
}

// TODO: Delete this function
export const useUpdatePolicy = (type: PolicyType, params?: Params) => {
  const { logger } = useLogger()
  const { useUpdate, queryUrl } = hooksMap[type]

  const context = useUpdate(params, undefined, (queryClient) => ({
    onSuccess: async (response) => {
      try {
        const { id, policyType = "Protect" } = response.data?.[0] || {} // default value "Protect" because CI policies have different response structure, empty response when patching entity

        if (id) {
          queryClient.setQueryData([`${queryUrl.LIST}/${id}`, {}], response)
          queryClient.refetchQueries({
            queryKey: [queryUrl.LIST, { policyType }]
          })
        }
      } catch (err) {
        logger.error(err as Error)
      }

      return Promise.resolve(response)
    }
  }))

  return context
}

export const useConditionsOptions = (
  params: Params<{
    type: PolicyType
    subType: SubType
    policyCategory?: PolicyCategory
  }>
) => {
  const { type } = params
  const { useConditionsOptions } = hooksMap[type]
  const policyCategory = getPolicyCategory(type)
  const { auth } = useServices()
  const isDemo = auth.isDemo() && type === "scm"
  const context = useConditionsOptions(
    { policyCategory, policyType: getPolicySubType(params.subType) },
    {
      select: isDemo
        ? (response: ConditionsOptionsPoliciesResponse) =>
            getConditionsOptions(response, { isDemo })
        : getConditionsOptions
    }
  )

  return context
}

export const useScopeOptions = (type: PolicyType) => {
  const { useScopeOptions: useScopeOptionsHook } = hooksMap[type]
  const context = useScopeOptionsHook({
    select: getScopeOptionsScm
  })

  return context
}

export const usePolicyActions = (attr: Attr) => {
  const { type, subType } = attr
  const { useDelete, useUpdate } = hooksMap[type]
  const remover = useDelete()
  const updater = useUpdate()
  const { confirm } = useConfirmModal()
  const { logger, EVENTS } = useLogger()
  const { message } = App.useApp()
  const { t } = useTranslation("translation", {
    keyPrefix: "governance.policies"
  })
  const events = EVENTS.ANALYTIC_EVENTS.GOVERNANCE.POLICIES[type] || {}

  const onDelete = (policy: Policy) => {
    const { id, name } = policy
    confirm((attr) =>
      DeleteConfirmModal({
        name,
        entityName: `${type}_${subType}`,
        onOk: async () => {
          await remover.mutateAsync(id)

          logger.info(events.DELETE, { id, name })
        },
        ...attr
      })
    )
  }

  const onUpdateList = async (
    policies: Policy[],
    props: { onError?: () => void } = {}
  ) => {
    const key = "update-list-use-policies-governance-page"

    try {
      message.loading({ key, content: t("saving"), duration: 0 })

      await updater.mutateAsync({
        policyType: getPolicySubType(subType),
        policyCategory: getPolicyCategory(type),
        policies: policies.map(({ id, enabled }) => ({ id, enabled }))
      })

      message.success({ key, content: t("saved") })
    } catch (err: any) {
      const detail = err?.data?.detail
      const content =
        typeof detail === "string" ? detail : t("error.serverError")

      message.error({ key, content })
      props.onError?.()
    }
  }

  return {
    events,
    logger,
    onUpdateList,
    onDelete,
    isUpdating: updater.isPending
  }
}
